import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = process.env.REACT_APP_DEFAULT_LOGO_URL;

// Helper function to get full image URL
const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${BACKEND_URL}${path}`;
};

const Vrienden = () => {
  const [settings, setSettings] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    help_with: [],
    availability: '',
    comments: '',
    privacy_consent: false,
    honeypot: '' // Anti-spam field
  });

  const helpOptions = [
    'Opbouw & afbraak evenementen',
    'Helpen aan bar of bediening',
    'Communicatie / social media / foto\'s nemen',
    'Sponsoring / partners aanspreken',
    'Administratieve hulp',
    'Maakt niet uit, zet me in waar nodig'
  ];

  const availabilityOptions = [
    '1x per jaar',
    'Een paar keer per jaar',
    'Regelmatig'
  ];

  useEffect(() => {
    fetchSettings();
    trackVisitor();
  }, []);

  const trackVisitor = async () => {
    try {
      await axios.post(`${API}/visitor/track`);
      const statsRes = await axios.get(`${API}/visitor/stats`);
      setVisitorStats(statsRes.data);
    } catch (error) {
      console.error('Error tracking visitor:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleCheckboxChange = (option) => {
    setFormData(prev => ({
      ...prev,
      help_with: prev.help_with.includes(option)
        ? prev.help_with.filter(item => item !== option)
        : [...prev.help_with, option]
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot (anti-spam)
    if (formData.honeypot) {
      toast.error('Spam detectie geactiveerd');
      return;
    }

    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.availability) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    if (!formData.privacy_consent) {
      toast.error('Je moet akkoord gaan met de privacy voorwaarden');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/volunteers`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || null,
        street: formData.street || null,
        postal_code: formData.postal_code || null,
        city: formData.city || null,
        help_with: formData.help_with,
        availability: formData.availability,
        comments: formData.comments || null,
        privacy_consent: formData.privacy_consent
      });

      setFormSubmitted(true);
      scrollToTop();
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        street: '',
        postal_code: '',
        city: '',
        help_with: [],
        availability: '',
        comments: '',
        privacy_consent: false,
        honeypot: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <a href="/" className="cursor-pointer">
                <img 
                  src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO}
                  alt="Logo" 
                  className="h-16 w-auto sm:h-20"
                />
              </a>
            </div>
            <div className="flex items-center gap-6">
              <a href="/" className="text-white hover:text-[#ffe980] font-medium transition-colors">
                Terug naar home
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Success Message */}
      {formSubmitted && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-2xl animate-in fade-in zoom-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Bedankt! 🎉</h3>
            <p className="text-gray-700 mb-6">
              Je aanmelding als vriend van het Feestcomité is goed ontvangen. We nemen zo snel mogelijk contact met je op.
            </p>
            <Button
              onClick={() => setFormSubmitted(false)}
              className="w-full bg-[#0a56c2] hover:bg-[#083d8a]"
            >
              Sluiten
            </Button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6">
            Word vriend van het<br />Feestcomité Leupegem! 🤝
          </h1>
          <div className="text-lg sm:text-xl text-gray-700 space-y-4 mb-8">
            <p>
              Het <strong>Feestcomité Leupegem</strong> organiseert met veel liefde en enthousiasme de mooiste evenementen voor jong en oud in onze gemeente. Van het Sinterklaasfeest tot de Nieuwjaarsreceptie en de jaarlijkse kermis – wij zorgen voor onvergetelijke momenten die Leupegem samenbrengen.
            </p>
            <p>
              Maar dat kunnen we niet alleen! <strong>We hebben jouw helpende handen nodig.</strong> Of je nu wilt helpen bij de opbouw, achter de bar wilt staan, foto's wilt maken of gewoon overal wilt inspringen waar nodig – elk handje helpt. Je kan zelf aangeven waarmee je wilt helpen én hoe vaak je beschikbaar bent. Geen verplichtingen, gewoon gezellig meehelpen wanneer het jou uitkomt!
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-[#0a56c2] p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              📝 Inschrijvingsformulier
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                style={{ position: 'absolute', left: '-9999px' }}
                tabIndex="-1"
                autoComplete="off"
              />

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voornaam <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    placeholder="Jouw voornaam"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Familienaam <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    placeholder="Jouw familienaam"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-mailadres <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    placeholder="jouw@email.be"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefoon / GSM
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="0412 34 56 78"
                  />
                </div>
              </div>

              {/* Address Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Straat + nummer
                </label>
                <Input
                  type="text"
                  value={formData.street}
                  onChange={(e) => setFormData({...formData, street: e.target.value})}
                  placeholder="Dorpsstraat 123"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postcode
                  </label>
                  <Input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    placeholder="9700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gemeente
                  </label>
                  <Input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Leupegem"
                  />
                </div>
              </div>

              {/* Help With Checkboxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Waarmee wil je helpen? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {helpOptions.map((option) => (
                    <div key={option} className="flex items-center">
                      <Checkbox
                        id={option}
                        checked={formData.help_with.includes(option)}
                        onCheckedChange={() => handleCheckboxChange(option)}
                        className="mr-3"
                      />
                      <label
                        htmlFor={option}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Availability Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hoe vaak kan je ongeveer helpen? <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.availability}
                  onChange={(e) => setFormData({...formData, availability: e.target.value})}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0a56c2]"
                >
                  <option value="">Selecteer een optie</option>
                  {availabilityOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opmerkingen of talenten die je wilt delen
                </label>
                <Textarea
                  value={formData.comments}
                  onChange={(e) => setFormData({...formData, comments: e.target.value})}
                  rows={4}
                  placeholder="Vertel ons meer over jezelf, je talenten, of andere opmerkingen..."
                />
              </div>

              {/* Privacy Consent */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Checkbox
                    id="privacy"
                    checked={formData.privacy_consent}
                    onCheckedChange={(checked) => setFormData({...formData, privacy_consent: checked})}
                    required
                    className="mt-1 mr-3"
                  />
                  <label
                    htmlFor="privacy"
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    <span className="text-red-500">*</span> Ik ga akkoord dat het Feestcomité Leupegem mijn gegevens bewaart en gebruikt om mij te contacteren over activiteiten en helpmomenten.
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0a56c2] hover:bg-[#083d8a] py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Bezig met verzenden...
                  </span>
                ) : (
                  'Meld je aan! 🎉'
                )}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                <span className="text-red-500">*</span> = verplicht veld
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <img 
              src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO}
              alt="Logo" 
              className="h-32 w-auto mx-auto mb-4 brightness-0 invert"
            />
          </div>
          <p className="text-gray-400 mb-6">Feestcomité Leupegem - Waar elk feest onvergetelijk wordt</p>
          <p className="text-sm text-gray-500 mt-8">© 2024 Feestcomité Leupegem. Alle rechten voorbehouden.</p>
          <p className="text-xs text-gray-600 mt-4">
            Development by{' '}
            <a 
              href="https://www.drukwerkbestellen.be" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#0a56c2] hover:text-[#083d8a] hover:underline transition-colors"
            >
              www.drukwerkbestellen.be
            </a>
          </p>
          {visitorStats && (
            <div className="mt-8 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-2">Bezoekers</p>
              <div className="flex justify-center gap-6 flex-wrap text-sm">
                <div>
                  <span className="text-gray-400">Vandaag: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_today}</span>
                </div>
                <div>
                  <span className="text-gray-400">Deze maand: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_month}</span>
                </div>
                <div>
                  <span className="text-gray-400">Dit jaar: </span>
                  <span className="text-white font-semibold">{visitorStats.visits_this_year}</span>
                </div>
                <div>
                  <span className="text-gray-400">Totaal: </span>
                  <span className="text-white font-semibold">{visitorStats.total_visits}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default Vrienden;
